import React from 'react';
import styled from 'styled-components';
import { colors, text } from '@annote/core/styles';

const NoteRef = styled.span`
  color: ${colors.text.link};
  font-weight: ${text.weight.medium};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const SourceRef = styled.span`
  color: ${colors.text.description};
  font-style: italic;
`;

const Count = styled.span`
  color: ${colors.text.placeholder};
  font-size: ${text.size.small};
  margin-left: 4px;
`;

export interface ParsedTitleSegment {
  type: 'text' | 'note' | 'source' | 'count';
  content: string;
}

/**
 * Parse suggestion title with note references.
 *
 * Format:
 * - [n Title] - note reference
 * - [s Title] - source reference
 * - x3 - count multiplier
 *
 * Examples:
 * - "Delete [n Untitled Note] x3"
 * - "Organize your Poems [n Poem about a dog], [n Poem for mom]"
 * - "Continue reading [s Neurons uncover...]"
 */
export const parseSuggestionTitle = (title: string): ParsedTitleSegment[] => {
  const segments: ParsedTitleSegment[] = [];

  // Pattern: [n Title], [s Title], or x\d+
  const pattern = /\[([ns])\s+([^\]]+)\]|x(\d+)/g;

  let lastIndex = 0;
  let match = pattern.exec(title);

  while (match !== null) {
    // Add text before this match
    if (match.index > lastIndex) {
      const textContent = title.substring(lastIndex, match.index);
      if (textContent.trim()) {
        segments.push({ type: 'text', content: textContent });
      }
    }

    // Add the matched reference
    if (match[1]) {
      // Note or source reference
      const refType = match[1] === 'n' ? 'note' : 'source';
      segments.push({ type: refType, content: match[2] });
    } else if (match[3]) {
      // Count reference
      segments.push({ type: 'count', content: `x${match[3]}` });
    }

    lastIndex = pattern.lastIndex;
    match = pattern.exec(title);
  }

  // Add remaining text
  if (lastIndex < title.length) {
    const textContent = title.substring(lastIndex);
    if (textContent.trim()) {
      segments.push({ type: 'text', content: textContent });
    }
  }

  return segments;
};

/**
 * Render parsed title as React elements
 */
export const renderParsedTitle = (segments: ParsedTitleSegment[]): React.ReactNode => {
  return segments.map((segment, index) => {
    switch (segment.type) {
      case 'note':
        return <NoteRef key={index}>{segment.content}</NoteRef>;
      case 'source':
        return <SourceRef key={index}>{segment.content}</SourceRef>;
      case 'count':
        return <Count key={index}>{segment.content}</Count>;
      default:
        return <span key={index}>{segment.content}</span>;
    }
  });
};
